<script setup>
import { ref, computed, onMounted } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import twentyApi from 'dashboard/api/integrations/twenty';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const { t } = useI18n();

const instanceUrl = ref('');
const apiKey = ref('');
const isLoading = ref(false);
const integrationLoaded = ref(false);

const integration = useFunctionGetter('integrations/getIntegration', 'twenty');
const isConnected = computed(() => !!integration.value?.enabled);
const logoSrc = computed(
  () =>
    `/dashboard/images/integrations/${integration.value?.logo || 'twenty.png'}`
);

const loadIntegration = async () => {
  await store.dispatch('integrations/get', 'twenty');
  integrationLoaded.value = true;
};

const handleConnect = async () => {
  if (!instanceUrl.value || !apiKey.value) return;
  isLoading.value = true;
  try {
    await twentyApi.connect(instanceUrl.value, apiKey.value);
    await loadIntegration();
    useAlert(t('INTEGRATION_SETTINGS.TWENTY.CONNECT.SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.TWENTY.CONNECT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleDisconnect = async () => {
  isLoading.value = true;
  try {
    await twentyApi.disconnect();
    await loadIntegration();
    useAlert(t('INTEGRATION_SETTINGS.TWENTY.DISCONNECT.SUCCESS'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadIntegration);
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div v-if="integrationLoaded">
      <div
        class="flex flex-col gap-6 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-center gap-6">
          <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
            <img :src="logoSrc" class="max-w-full rounded-md" />
          </div>
          <div>
            <h3 class="mb-1 text-xl font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TWENTY.HEADER') }}
            </h3>
            <p class="text-n-slate-11 text-sm leading-6">
              {{ $t('INTEGRATION_SETTINGS.TWENTY.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div v-if="isConnected" class="flex items-center justify-between">
          <span class="text-sm text-green-600 font-medium">
            {{ $t('INTEGRATION_SETTINGS.TWENTY.CONNECT.CONNECTED') }}
          </span>
          <Button
            faded
            ruby
            :label="$t('INTEGRATION_SETTINGS.TWENTY.DISCONNECT.BUTTON')"
            :is-loading="isLoading"
            @click="handleDisconnect"
          />
        </div>

        <form
          v-else
          class="flex flex-col gap-4"
          @submit.prevent="handleConnect"
        >
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TWENTY.CONNECT.INSTANCE_URL_LABEL') }}
            </label>
            <input
              v-model="instanceUrl"
              type="url"
              required
              class="w-full text-sm border border-n-weak rounded px-3 py-2 bg-n-background focus:outline-none focus:ring-1 focus:ring-woot-500"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.TWENTY.CONNECT.INSTANCE_URL_PLACEHOLDER'
                )
              "
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TWENTY.CONNECT.API_KEY_LABEL') }}
            </label>
            <input
              v-model="apiKey"
              type="password"
              required
              class="w-full text-sm border border-n-weak rounded px-3 py-2 bg-n-background focus:outline-none focus:ring-1 focus:ring-woot-500"
              :placeholder="
                $t('INTEGRATION_SETTINGS.TWENTY.CONNECT.API_KEY_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <Button
              type="submit"
              faded
              blue
              :label="$t('INTEGRATION_SETTINGS.TWENTY.CONNECT.BUTTON')"
              :is-loading="isLoading"
            />
          </div>
        </form>
      </div>
    </div>
    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
